import { NewOrder, OrderDetail, Order, Menu } from "@/api";
import store from "@/store/index";
import { Module, VuexModule, Mutation, Action, getModule } from "vuex-module-decorators";
import { CartEditStatus } from "@/types";

export interface IOrderModule {
  _newOrders: NewOrder[];
  _newOrderBases: Partial<NewOrder>[];
  _newOrder: NewOrder | null;
  _editStatus: CartEditStatus;
  _orders: Order[];
  _modalOrders: Order[];
  _modalOrdersQuantity: number;
  _order: Order | null;
  _isAddedCart: boolean;
  _isNotice: boolean;
  _currentDate: Date | null;
}

@Module({
  dynamic: true,
  store,
  name: "order",
  namespaced: true,
  preserveState: localStorage.getItem("vuex") ? JSON.parse(String(localStorage.getItem("vuex")))?.order : false,
})
class OrderModule extends VuexModule implements IOrderModule {
  public _newOrders: NewOrder[] = [];

  public _newOrderBases: Partial<NewOrder>[] = [];

  public _newOrder: NewOrder | null = null;

  public _orders: Order[] = [];

  public _modalOrders: Order[] = [];

  public _modalOrdersQuantity = 0;

  public _editStatus: CartEditStatus = CartEditStatus.Edit;

  public _order: Order | null = null;

  public _isAddedCart = false;

  public _isNotice = false;

  public _isPush = false;

  public _isNewOrdersModal = false;

  public _currentDate: Date | null = null;

  @Mutation
  public changeNewOrders(newOrders: NewOrder[]) {
    this._newOrders = newOrders || null;
  }

  @Mutation
  public addNewOrder(newOrder: NewOrder) {
    this._newOrders.push(newOrder);
  }

  @Mutation
  public changeNewOrderBases(newOrders: NewOrder[]) {
    this._newOrders = newOrders;
  }

  @Mutation
  public changeNewOrder(newOrder: NewOrder) {
    this._newOrder = newOrder || null;
  }

  @Mutation
  public addNewOrderBase(newOrder: NewOrder) {
    this._newOrders.push(newOrder);
  }

  @Mutation
  public changeOrders(orders: Order[]) {
    this._orders = orders || [];
  }

  @Mutation
  public changeOrder(order: Order) {
    this._order = order || null;
  }

  @Mutation
  public changeModalOrders(modalOrders: Order[]) {
    this._modalOrders = modalOrders || [];
  }

  @Mutation
  public changeModalOrdersQuantity(modalOrdersQuantity: number) {
    this._modalOrdersQuantity = modalOrdersQuantity || 0;
  }

  @Mutation
  public changeEditStatus(editStatus: CartEditStatus) {
    this._editStatus = editStatus || CartEditStatus.Edit;
  }

  @Mutation
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public replaceOrderDetails(payload: { restaurantUuid: string; orderDetails: any[] }) {
    const { restaurantUuid, orderDetails } = payload;
    const newOrders = this._newOrders.map(newOrder => {
      if (newOrder?.restaurantUuid === restaurantUuid) {
        // eslint-disable-next-line no-param-reassign
        newOrder.orderDetails = orderDetails;
        return newOrder;
      }
      return newOrder;
    });
    this._newOrders = newOrders;
  }

  @Mutation
  public changeIsAddedCart(isAddedCart: boolean) {
    this._isAddedCart = isAddedCart || false;
  }

  @Mutation
  public changeIsNotice(isNotice: boolean) {
    this._isNotice = isNotice || false;
  }

  @Mutation
  public changeIsPush(isPush: boolean) {
    this._isPush = isPush || false;
  }

  @Mutation
  public changeIsNewOrdersModal(isNewOrdersModal: boolean) {
    this._isNewOrdersModal = isNewOrdersModal || false;
  }

  @Mutation
  public changeCurrentDate(currentDate: Date | null) {
    this._currentDate = currentDate;
  }

  @Action({ rawError: true })
  public commitNewOrders(newOrders: Partial<NewOrder>[]) {
    this.context.commit("changeNewOrders", newOrders);
  }

  @Action({ rawError: true })
  public commitAddNewOrder(newOrder: NewOrder) {
    this.context.commit("addNewOrder", newOrder);
  }

  @Action({ rawError: true })
  public commitNewOrder(newOrder: Partial<NewOrder> | null) {
    this.context.commit("changeNewOrder", newOrder);
  }

  @Action({ rawError: true })
  public commitNewOrderBases(newOrders: NewOrder[]) {
    this.context.commit("changeNewOrders", newOrders);
  }

  @Action({ rawError: true })
  public commitOrders(orders: Order[]) {
    this.context.commit("changeOrders", orders);
  }

  @Action({ rawError: true })
  public commitModalOrders(modalOrders: Order[]) {
    this.context.commit("changeModalOrders", modalOrders);
  }

  @Action({ rawError: true })
  public commitModalOrdersQuantity(modalOrders: Order[]) {
    this.context.commit("changeModalOrdersQuantity", modalOrders.length);
  }

  @Action({ rawError: true })
  public commitAddOrderDetail(payload: { restaurantUuid: string; orderDetail: OrderDetail }) {
    const { restaurantUuid } = payload;
    const { orderDetail } = payload;

    const newOrders = JSON.parse(JSON.stringify(this._newOrders)) as Partial<NewOrder>[];
    if (newOrders.length > 0) {
      newOrders.forEach(order => {
        // 一致した場合
        if (order.restaurantUuid === restaurantUuid) {
          const { menuUuid } = orderDetail;
          const targetMenu = order.orderDetails?.find(detail => detail.menuUuid === menuUuid);
          if (!order.orderDetails) {
            // eslint-disable-next-line no-param-reassign
            order.orderDetails = [];
          }
          // すでにmenuが存在していれば個数分追加
          if (targetMenu && orderDetail.quantity) {
            if (!targetMenu.quantity) {
              targetMenu.quantity = orderDetail.quantity;
            }
            targetMenu.quantity += orderDetail.quantity;
            // eslint-disable-next-line no-param-reassign
            order.orderDetails = order.orderDetails.filter(fOrderDetail => {
              return fOrderDetail.menuUuid !== targetMenu.menuUuid;
            });
            order.orderDetails.push(targetMenu);
          } else if (targetMenu) {
            targetMenu.quantity = orderDetail.quantity;
            order.orderDetails.push(targetMenu);
          } else {
            order.orderDetails.push(orderDetail);
          }
          this.context.commit("replaceOrderDetails", {
            restaurantUuid,
            orderDetails: order.orderDetails,
          });
        } else {
          // 他店舗のメニューが追加されていた場合
          const newOrder = {
            restaurantUuid,
            orderDetails: [orderDetail],
          };
          const currentNewOrders = JSON.parse(JSON.stringify(this._newOrders));
          if (
            currentNewOrders.some((currentOrder: NewOrder) => {
              return restaurantUuid === currentOrder.restaurantUuid;
            })
          ) {
            return;
          }
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          this.commitAddNewOrder(newOrder as any);
        }
      });
    } else {
      const newOrder = {
        restaurantUuid,
        orderDetails: [orderDetail],
      };
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      this.commitAddNewOrder(newOrder as any);
    }
  }

  @Action({ rawError: true })
  public commitRemoveOrderDetail(orderDetail: OrderDetail & { menu: Menu }) {
    // 対象レストランのorderを抽出
    const findedOrder = this.newOrders.find(order => {
      return order.restaurantUuid === orderDetail.menu.restaurantUuid;
    });
    if (!findedOrder) {
      return;
    }
    const currentOrderDetails = findedOrder?.orderDetails;
    // orderDetailを削除
    const newOrderDetails = currentOrderDetails?.filter(od => {
      return od.menuUuid !== orderDetail.menuUuid;
    });

    if (newOrderDetails && newOrderDetails.length > 0) {
      const newOrders = this.newOrders.filter(order => {
        return order.restaurantUuid !== orderDetail.menu.restaurantUuid;
      });
      const newOrder = { ...findedOrder, orderDetails: newOrderDetails };
      newOrders.push(newOrder);
      this.context.commit("changeNewOrders", newOrders);
    } else {
      const newOrders = this.newOrders.filter(order => {
        return order.restaurantUuid !== orderDetail.menu.restaurantUuid;
      });
      this.context.commit("changeNewOrders", newOrders);
    }
  }

  @Action({ rawError: true })
  public commitAddNewOrderBase(newOrder: NewOrder) {
    this.context.commit("addNewOrder", newOrder);
  }

  @Action({ rawError: true })
  public commitOrder(order: Order | null) {
    this.context.commit("changeOrder", order);
  }

  @Action({ rawError: true })
  public commitEditStatus(editStatus: CartEditStatus) {
    this.context.commit("changeEditStatus", editStatus);
  }

  @Action({ rawError: true })
  public commitIsAddedCart(isAddedCart: boolean) {
    this.context.commit("changeIsAddedCart", isAddedCart);
  }

  @Action({ rawError: true })
  public commitIsNotice(isNotice: boolean) {
    this.context.commit("changeIsNotice", isNotice);
  }

  @Action({ rawError: true })
  public commitIsPush(isPush: boolean) {
    this.context.commit("changeIsPush", isPush);
  }

  @Action({ rawError: true })
  public commitIsNewOrdersModal(isNewOrdersModal: boolean) {
    this.context.commit("changeIsNewOrdersModal", isNewOrdersModal);
  }

  @Action({ rawError: true })
  public commitCurrentDate(currentDate: Date | null) {
    this.context.commit("changeCurrentDate", currentDate);
  }

  public get newOrders() {
    return this._newOrders || [];
  }

  public get newOrder() {
    return this._newOrder || null;
  }

  public get orders() {
    return this._orders || [];
  }

  public get modalOrders() {
    return this._modalOrders || [];
  }

  public get modalOrdersQuantity() {
    return this._modalOrdersQuantity || 0;
  }

  public get order() {
    return this._order || null;
  }

  public get editStatus() {
    return this._editStatus || CartEditStatus.Edit;
  }

  public get isAddedCart() {
    return this?._isAddedCart ? this._isAddedCart : false;
  }

  public get isNotice() {
    return this?._isNotice ? this._isNotice : false;
  }

  public get isPush() {
    return this?._isPush ? this._isPush : false;
  }

  public get isNewOrdersModal() {
    return this?._isNewOrdersModal ? this._isNewOrdersModal : false;
  }

  public get currentDate() {
    return this._currentDate;
  }
}

export default getModule(OrderModule);
